import { Stack } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select/SelectInput';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TCreateDeviceFormProps } from '@/console/features/devices/components/create-device/create-device-form.types';
import { TCreateDeviceInputSchema } from '@/console/features/devices/components/create-device/create-device.types';
import { createDeviceInputSchema } from '@/console/features/devices/components/create-device/create-device.validation';
import { DeviceCredentialPicker } from '@/console/features/devices/components/device-credential-picker/device-credential-picker';
import { DynamicForm } from '@/console/features/dynamic-form/dynamic-form';
import { FormSelectSecret } from '@/console/features/vaults/components/form-select-secret/form-select-secret';
import { FormInput } from '@/shared/components/ui/form/components/form-input/form-input';
import { FormSelect } from '@/shared/components/ui/form/components/form-select/form-select';
import { useCustomForm } from '@/shared/components/ui/form/context/form-context';
import { GroupSelect } from '@/shared/features/groups/components/group-select/group-select';
import { TagSelect } from '@/shared/features/tags/components/tag-select/tag-select';
import { useDynamicForm } from '@/shared/hooks/use-dynamic-form/use-dynamic-form';
import {
  useVendor,
  useVendorOptions,
} from '@/shared/hooks/use-vendor/use-vendor';
import { mapIdeOptionsWithInfo } from '@/shared/hooks/use-vendor/use-vendor.utils';
import { DeviceTypeEnum, InputTypeEnum } from '@/shared/types/types.generated';

export const CreateDeviceForm: React.FC<TCreateDeviceFormProps> = ({
  disableVendorSelect,
  groupId,
}) => {
  const { resetField, control, setValue } =
    useFormContext<TCreateDeviceInputSchema>();
  const { t } = useTranslation();
  const {
    getVendorOptions,
    isLoading: vendorLoading,
    getVendorIDEOptions,
    getDeviceTypesOptions,
  } = useVendorOptions();
  const { getDevice } = useVendor();
  const { createDynamicSchema } = useDynamicForm();
  const { updateSchema } = useCustomForm();
  const [vendorId, ideConfigId, deviceType] = useWatch({
    control,
    name: ['vendorId', 'ideConfigId', 'deviceType'],
  });
  const device = getDevice(vendorId, ideConfigId, deviceType);

  const handleVendorChange = (event: SelectChangeEvent<unknown>) => {
    const vendorId = event.target.value as string;
    const ideOptions = getVendorIDEOptions(vendorId) ?? [];

    if (ideOptions?.length > 1) {
      resetField('ideConfigId');
      resetField('deviceType');
      return;
    }

    const ideConfigId = ideOptions[0]?.value;
    setValue('ideConfigId', ideConfigId);

    handleIdeConfigChange(vendorId, ideConfigId);
  };

  const handleIdeConfigChange = (vendorId: string, ideConfigId: string) => {
    const deviceTypesOptions =
      getDeviceTypesOptions(vendorId, ideConfigId) ?? [];

    if (deviceTypesOptions?.length > 1) {
      resetField('deviceType');
      return;
    }

    setValue('deviceType', deviceTypesOptions[0]?.value);
  };

  const handleDeviceTypeChange = (deviceType: DeviceTypeEnum) => {
    const device = getDevice(vendorId, ideConfigId, deviceType);
    const additionalInputs = device?.additionalInputs;

    if (!additionalInputs) {
      return;
    }

    const dynamicSchema = createDynamicSchema(additionalInputs);
    const newSchema = createDeviceInputSchema.extend({
      metaData: dynamicSchema.default({}),
    });

    updateSchema(newSchema);
  };

  const deviceTypesOptions = getDeviceTypesOptions(vendorId, ideConfigId) ?? [];
  const ideOptions = mapIdeOptionsWithInfo(getVendorIDEOptions(vendorId) ?? []);
  const vendorOptions = getVendorOptions() ?? [];
  const additionalInputs = device?.additionalInputs;
  const credentialInputs =
    additionalInputs?.filter(
      (input) => input.inputType === InputTypeEnum.Credential,
    ) ?? [];
  const nonCredentialInputs =
    additionalInputs?.filter(
      (input) => input.inputType !== InputTypeEnum.Credential,
    ) ?? [];

  return (
    <>
      <FormInput<TCreateDeviceInputSchema>
        name="name"
        label={t('common:name')}
        required
      />
      <FormInput<TCreateDeviceInputSchema>
        name="description"
        label={t('common:description')}
      />
      <Stack direction="row" gap="0.75rem">
        <FormSelect<TCreateDeviceInputSchema>
          name="vendorId"
          label={t('common:vendor')}
          options={vendorOptions}
          disabled={disableVendorSelect || vendorLoading}
          onChange={handleVendorChange}
          required
        />
        <FormSelect<TCreateDeviceInputSchema>
          name="ideConfigId"
          label={t('common:ide')}
          required
          disabled={disableVendorSelect || !vendorId}
          onChange={(event) =>
            handleIdeConfigChange(vendorId, event.target.value as string)
          }
          options={ideOptions}
        />
      </Stack>
      {deviceTypesOptions.length > 0 && (
        <FormSelect<TCreateDeviceInputSchema>
          name="deviceType"
          label={t('device:device_type')}
          options={deviceTypesOptions}
          required
          onChange={(event) =>
            handleDeviceTypeChange(event.target.value as DeviceTypeEnum)
          }
        />
      )}
      {nonCredentialInputs.length > 0 && (
        <DynamicForm options={nonCredentialInputs} formNamePrefix="metaData" />
      )}
      <TagSelect<TCreateDeviceInputSchema> name="tags" entity="Device" />
      <GroupSelect<TCreateDeviceInputSchema>
        name="groupId"
        getItemId={(value) => value}
        label={t('groups:group')}
      />
      <Stack gap="1rem">
        <FormSelectSecret<TCreateDeviceInputSchema>
          label={t('device:connection_credentials')}
          headerLabel={t('device:connection_credentials')}
          groupId={groupId}
          allowCreateSecret
        />
        {credentialInputs.map((input) => (
          <DeviceCredentialPicker key={input.value} input={input} />
        ))}
      </Stack>
    </>
  );
};
